// While Copilot works: the step it is on and how long it has been working,
// the last few steps and the latest note always in view (a long turn is never
// a bare spinner), and every step behind "Show every step". The team's
// internal hand-offs stay out of it; the full page shows them in Progress.
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toDisplayBrand } from "../../utils/displayBrand";
import { FONT, FIG_INK, FIG_MUTED, HAIRLINE } from "../../design";
import { Spinner, TextButton } from "./CardStyle";
import { DoneMark } from "./CardIcons";

const Box = styled.div`
  font-family: ${FONT};
  border: 1px solid ${HAIRLINE};
  border-radius: 14px;
  padding: 11px 13px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  .now { display: flex; align-items: center; gap: 10px; font-size: 13.5px; font-weight: 600; color: ${FIG_INK}; }
  .now .label { flex: 1; min-width: 0; }
  .now small { font-size: 11.5px; font-weight: 500; color: ${FIG_MUTED}; white-space: nowrap; }
  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
  li { display: flex; align-items: flex-start; gap: 8px; font-size: 12.5px; color: #4e4b48; line-height: 1.45; }
  li.thought { color: ${FIG_INK}; background: #f7f7f9; border-radius: 8px; padding: 6px 8px; }
`;

const tidy = (t) => {
  const s = toDisplayBrand(String(t || "")).replace(/\s+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

/** The plain-language steps of this turn, oldest first, without hand-offs. */
export function stepsOf(activity = []) {
  const out = [];
  for (const e of activity) {
    if (!e || !e.title || ["message"].includes(e.kind) || e.role === "executor") continue;
    const step = { id: e.entry_id, text: tidy(e.kind === "thought" ? e.body || e.title : e.title), thought: e.kind === "thought", status: e.status, ts: e.ts };
    // The same step again right after itself reads once, with how many times.
    const prev = out[out.length - 1];
    if (prev && !prev.thought && !step.thought && prev.base === step.text) {
      prev.times += 1;
      prev.text = `${prev.base} (${prev.times})`;
      prev.ts = step.ts || prev.ts;
      continue;
    }
    out.push({ ...step, base: step.text, times: 1 });
  }
  return out;
}

// Seconds without a new step before the line says Copilot is thinking.
const QUIET_SECONDS = 8;
const tsOf = (v) => (v == null ? null : typeof v === "number" ? (v < 1e12 ? v * 1000 : v) : new Date(v).getTime());

const WorkingLine = ({ activity = [], interim = [], started }) => {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const steps = stepsOf(activity);
  const doing = [...steps].reverse().find((s) => !s.thought);
  // A quiet stretch after the last step (the answer being worked out, or a
  // specialist reading): say so rather than repeat a finished step.
  const lastAt = Math.max(started || 0, ...activity.map((e) => tsOf(e && (e.ts || e.createdAt)) || 0));
  const quiet = doing && lastAt && now - lastAt > QUIET_SECONDS * 1000;
  const secs = started ? Math.max(0, Math.round((now - started) / 1000)) : null;
  const elapsed = secs == null ? "" : secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  const notes = interim.map((t, i) => ({ id: `i${i}`, text: tidy(t), thought: true }));
  const all = [...steps, ...notes];
  // Always in view: the three steps before the current one, then the latest note.
  const done = steps.filter((x) => !x.thought && (quiet || x !== doing));
  const lastNote = [...all].reverse().find((x) => x.thought);
  const recent = [...done.slice(-3), ...(lastNote ? [lastNote] : [])];
  const shown = open ? all : recent;
  return (
    <Box aria-live="polite">
      <div className="now">
        <Spinner $size={16} />
        <span className="label">{quiet ? "Thinking it through" : doing ? doing.text : "Working on it"}</span>
        {elapsed ? <small>{elapsed}</small> : null}
      </div>
      {shown.length ? (
        <ul>
          {shown.map((s) => (
            <li key={s.id} className={s.thought ? "thought" : ""}>
              {s.thought ? null : <DoneMark size={14} />}
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {all.filter((x) => quiet || x !== doing).length > recent.length ? <TextButton type="button" style={{ alignSelf: "flex-start" }} onClick={() => setOpen((v) => !v)}>{open ? "Show fewer steps" : "Show every step"}</TextButton> : null}
    </Box>
  );
};

export default WorkingLine;
